import { defineMutator } from "@rocicorp/zero";
import { z } from "zod";
import { zql } from "../schema";
import { ADMIN_PLUS } from "../constants";

export const notificationKeywordsMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      user_id: z.string(),
      workspace_id: z.string(),
      keyword: z.string(),
      created_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: user_id mismatch");
      }
      await tx.mutate.notification_keywords.insert(args);
    },
  ),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const entity = await tx.run(
          zql.notification_keywords.where("id", args.id).one(),
        );
        if (!entity) return;
        if (entity.user_id !== ctx.sub) {
          throw new Error("Unauthorized: not owner");
        }
      }
      await tx.mutate.notification_keywords.delete(args);
    },
  ),
};

export const threadFollowersMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      thread_id: z.string(),
      user_id: z.string(),
      followed_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: user_id mismatch");
      }
      if (tx.location === "server") {
        // thread_id references a message — verify channel membership
        const threadMsg = await tx.run(
          zql.messages.where("id", args.thread_id).one(),
        );
        if (!threadMsg) throw new Error("Thread not found");
        const membership = await tx.run(
          zql.channel_members
            .where("channel_id", threadMsg.channel_id)
            .where("user_id", ctx.sub)
            .where("removed_at", "IS", null)
            .one(),
        );
        if (!membership) {
          throw new Error("Unauthorized: not a channel member");
        }
      }
      await tx.mutate.thread_followers.insert(args);
    },
  ),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const entity = await tx.run(
          zql.thread_followers.where("id", args.id).one(),
        );
        if (!entity) return;
        if (entity.user_id !== ctx.sub) {
          throw new Error("Unauthorized: not owner");
        }
      }
      await tx.mutate.thread_followers.delete(args);
    },
  ),
};

export const pinsMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      message_id: z.string(),
      channel_id: z.string(),
      pinned_by: z.string(),
      pinned_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.pinned_by !== ctx.sub) {
        throw new Error("Unauthorized: pinned_by mismatch");
      }
      if (tx.location === "server") {
        // Derive authoritative channel_id from message
        const msg = await tx.run(
          zql.messages.where("id", args.message_id).one(),
        );
        if (!msg) throw new Error("Message not found");
        if (msg.channel_id !== args.channel_id) {
          throw new Error("Unauthorized: channel_id mismatch with message");
        }
        const membership = await tx.run(
          zql.channel_members
            .where("channel_id", msg.channel_id)
            .where("user_id", ctx.sub)
            .where("removed_at", "IS", null)
            .one(),
        );
        if (!membership) {
          throw new Error("Unauthorized: not a channel member");
        }
      }
      await tx.mutate.pins.insert(args);
    },
  ),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const entity = await tx.run(zql.pins.where("id", args.id).one());
        if (!entity) return;
        // Pin owner can always unpin
        if (entity.pinned_by === ctx.sub) {
          // allowed
        } else {
          // Workspace admin/owner OR channel manager can also unpin
          const channel = await tx.run(
            zql.channels.where("id", entity.channel_id).one(),
          );
          let isAdmin = false;
          if (channel?.workspace_id) {
            const wsRole = await tx.run(
              zql.workspace_members
                .where("workspace_id", channel.workspace_id)
                .where("user_id", ctx.sub)
                .where("removed_at", "IS", null)
                .one(),
            );
            isAdmin = !!wsRole && ADMIN_PLUS.includes(wsRole.role);
          }
          if (!isAdmin) {
            const myMembership = await tx.run(
              zql.channel_members
                .where("channel_id", entity.channel_id)
                .where("user_id", ctx.sub)
                .where("removed_at", "IS", null)
                .one(),
            );
            if (!myMembership?.is_channel_manager) {
              throw new Error(
                "Unauthorized: not pin owner, admin, or channel manager",
              );
            }
          }
        }
      }
      await tx.mutate.pins.delete(args);
    },
  ),
};

export const channelPostingPermissionsMutators = {
  insert: defineMutator(
    z.object({
      id: z.string(),
      channel_id: z.string(),
      user_id: z.string(),
      granted_by: z.string(),
      granted_at: z.number(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.granted_by !== ctx.sub) {
        throw new Error("Unauthorized: granted_by mismatch");
      }
      if (tx.location === "server") {
        // Must be admin or channel manager to grant posting permissions.
        // Mirrors the delete mutator's structural pattern below: workspace
        // channels go through admin/manager check; DMs and missing channels
        // throw in the else branch.
        const channel = await tx.run(
          zql.channels.where("id", args.channel_id).one(),
        );
        if (channel?.workspace_id) {
          const wsRole = await tx.run(
            zql.workspace_members
              .where("workspace_id", channel.workspace_id)
              .where("user_id", ctx.sub)
              .where("removed_at", "IS", null)
              .one(),
          );
          if (wsRole && ADMIN_PLUS.includes(wsRole.role)) {
            // Admin+ — allowed
          } else {
            const myMembership = await tx.run(
              zql.channel_members
                .where("channel_id", args.channel_id)
                .where("user_id", ctx.sub)
                .where("removed_at", "IS", null)
                .one(),
            );
            if (!myMembership?.is_channel_manager) {
              throw new Error("Unauthorized: not admin or channel manager");
            }
          }
        } else {
          // DM or missing channel — no admins or channel managers exist
          throw new Error("Unauthorized: not admin or channel manager");
        }
      }
      await tx.mutate.channel_posting_permissions.insert(args);
    },
  ),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const entity = await tx.run(
          zql.channel_posting_permissions.where("id", args.id).one(),
        );
        if (!entity) return;
        // Granter can always delete their own grants
        if (entity.granted_by === ctx.sub) {
          // allowed
        } else {
          // Workspace admin/owner can also delete (NOT channel-manager)
          const channel = await tx.run(
            zql.channels.where("id", entity.channel_id).one(),
          );
          if (channel?.workspace_id) {
            const wsRole = await tx.run(
              zql.workspace_members
                .where("workspace_id", channel.workspace_id)
                .where("user_id", ctx.sub)
                .where("removed_at", "IS", null)
                .one(),
            );
            if (!wsRole || !ADMIN_PLUS.includes(wsRole.role)) {
              throw new Error("Unauthorized: not granter or workspace admin");
            }
          } else {
            throw new Error("Unauthorized: not granter or workspace admin");
          }
        }
      }
      await tx.mutate.channel_posting_permissions.delete(args);
    },
  ),
};

export const notificationPreferencesMutators = {
  upsert: defineMutator(
    z.object({
      user_id: z.string(),
      workspace_id: z.string(),
      global_level: z.string().optional(),
      email_enabled: z.boolean().optional(),
      email_delay_minutes: z.number().optional(),
      desktop_enabled: z.boolean().optional(),
      mobile_enabled: z.boolean().optional(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: user_id mismatch");
      }
      await tx.mutate.notification_preferences.upsert({
        user_id: args.user_id,
        workspace_id: args.workspace_id,
        global_level: args.global_level ?? "mentions_dms",
        email_enabled: args.email_enabled ?? true,
        email_delay_minutes: args.email_delay_minutes ?? 15,
        desktop_enabled: args.desktop_enabled ?? true,
        mobile_enabled: args.mobile_enabled ?? true,
      });
    },
  ),
};

export const dndSettingsMutators = {
  upsert: defineMutator(
    z.object({
      user_id: z.string(),
      enabled: z.boolean().optional(),
      start_time: z.string().optional().nullable(),
      end_time: z.string().optional().nullable(),
      until: z.number().optional().nullable(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: user_id mismatch");
      }
      await tx.mutate.dnd_settings.upsert({
        user_id: args.user_id,
        enabled: args.enabled ?? false,
        start_time: args.start_time ?? null,
        end_time: args.end_time ?? null,
        until: args.until ?? null,
      });
    },
  ),
};

export const channelNotificationOverridesMutators = {
  upsert: defineMutator(
    z.object({
      id: z.string(),
      user_id: z.string(),
      channel_id: z.string(),
      level: z.string(),
      desktop_level: z.string().optional().nullable(),
      mobile_level: z.string().optional().nullable(),
    }),
    async ({ tx, args, ctx }) => {
      if (ctx?.sub && args.user_id !== ctx.sub) {
        throw new Error("Unauthorized: user_id mismatch");
      }
      if (tx.location === "server") {
        // Block override row hijack via id-collision upsert.
        const existing = await tx.run(
          zql.channel_notification_overrides.where("id", args.id).one(),
        );
        if (existing && existing.user_id !== ctx.sub) {
          throw new Error(
            "Unauthorized: cannot upsert another user's notification override",
          );
        }
      }
      await tx.mutate.channel_notification_overrides.upsert({
        id: args.id,
        user_id: args.user_id,
        channel_id: args.channel_id,
        level: args.level,
        desktop_level: args.desktop_level ?? null,
        mobile_level: args.mobile_level ?? null,
      });
    },
  ),
  delete: defineMutator(
    z.object({ id: z.string() }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const entity = await tx.run(
          zql.channel_notification_overrides.where("id", args.id).one(),
        );
        if (!entity) return;
        if (entity.user_id !== ctx.sub) {
          throw new Error("Unauthorized: not owner");
        }
      }
      await tx.mutate.channel_notification_overrides.delete(args);
    },
  ),
};

export const notificationEventsMutators = {
  update: defineMutator(
    z.object({
      id: z.string(),
      read_at: z.number().optional().nullable(),
    }),
    async ({ tx, args, ctx }) => {
      if (tx.location === "server") {
        const entity = await tx.run(
          zql.notification_events.where("id", args.id).one(),
        );
        if (!entity) return;
        if (entity.recipient_user_id !== ctx.sub) {
          throw new Error("Unauthorized: not recipient");
        }
      }
      await tx.mutate.notification_events.update(args);
    },
  ),
};
